import React, {useContext, useRef, useState} from "react";
import {
    Alert,
    Box,
    Button,
    ClickAwayListener,
    Collapse,
    Grow,
    IconButton,
    MenuItem,
    MenuList,
    Paper,
    Popper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {blockUser, change, changeAdminStatus, saveOrUpdateChild, detachChild} from "../../http/userAPI";
import ModalPoints from "./ModalPoints";
import {deleteCardId, matchPatient} from "../../http/patientAPI";
import ModalMatchCard from "./ModalMathCard";
import {Context} from "../..";
import ModalMatchCardByPatientId from "./ModalMatchCardByPatientId";
import ModalChildSettings from "./ModalChildSettings";
import ModalChangeName from "./ModalChangeName";

const MENU_OPTIONS = [
    "Начислить баллы",
    "Списать баллы",
    "Заблокировать пользователя",
    "Разблокировать пользователя",
    "Удалить сопоставление с картой",
    "Сопоставить с картой",
    "Сопоставить по ID пациента",
    "Изменить имя",
    "Выдать права администратора",
    "Убрать права администратора",
    "Добавить ребенка",
    "Отвязать ребенка от родителя",
];

const PatientSettings = React.memo(
    ({clinic, patient, handleClickGetPatientInfo, setPatientPoints}) => {
        const [isMenuOpen, setIsMenuOpen] = useState(false);
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [isMatchModalOpen, setIsMatchModalOpen] = useState(false);
        const [isMatchByCardModalOpen, setIsMatchByCardModalOpen] = useState(false);
        const [isChildModalOpen, setIsChildModalOpen] = useState(false);
        const [isChangeNameModalOpen, setIsChangeNameModalOpen] = useState(false);
        const [editingChild, setEditingChild] = useState(null);
        const [operationType, setOperationType] = useState("");
        const [error, setError] = useState(null);
        const [successMessage, setSuccessMessage] = useState(null);
        const anchorRef = useRef(null);
        const {user} = useContext(Context)

        const handleUpdatePoints = (points, direction) => {
            if (setPatientPoints) {
                setPatientPoints(prevPoints => {
                    if (direction === 'add') {
                        return prevPoints + points;
                    } else if (direction === 'reduce') {
                        return prevPoints - points;
                    }
                    return prevPoints;
                });
            }
        };

        const handleMatchCardId = async (id, phone, birthday) => {
            setError(null);
            setSuccessMessage(null);

            if (!patient?.phone) {
                setError("Номер телефона пользователя не указан");
                return;
            }

            if(!clinic.disableUserBirthday && !birthday) {
               setError("Заполните дату рождения пациента");
               return;
            }

            if (!phone) {
                setError("Заполните номер телефона пациента");
                return;
            }

            try {
                const response = await matchPatient(id, phone, birthday, null);
                if (response?.error) throw new Error(response.error);
                await handleClickGetPatientInfo();
                setSuccessMessage("Сопоставление с картой успешно");
                setTimeout(() => setSuccessMessage(null), 3000);
            } catch (error) {
                setError(error.message || "Ошибка при сопоставлении");
                setTimeout(() => setError(null), 3000);
            }
        };

        const handleMatchCardIdByPatientId = async (id, patientId) => {
            setError(null);
            setSuccessMessage(null);

            if (!patient?.phone) {
                setError("ID пациента не указан");
                return;
            }

            if (!patientId) {
                setError("Заполните все поля");
                return;
            }

            try {
                const response = await matchPatient(id, null, null, patientId);
                if (response?.error) throw new Error(response.error);
                await handleClickGetPatientInfo();
                setSuccessMessage("Сопоставление с картой успешно");
                setTimeout(() => setSuccessMessage(null), 3000);
            } catch (error) {
                setError(error.message || "Ошибка при сопоставлении");
                setTimeout(() => setError(null), 3000);
            }
        };

        const handleAddAdmin = async (needToBeAdmin) => {
            setError(null);
            setSuccessMessage(null);

            try {
                const data = await changeAdminStatus(patient.id, needToBeAdmin);
                patient.isAdmin = data.isAdmin;
                await handleClickGetPatientInfo();
                setSuccessMessage(
                    `Права администратора ${needToBeAdmin ? "выданы" : "убраны"}`
                );

                setTimeout(() => setSuccessMessage(null), 3000);
            } catch (error) {
                setError(error.message || "Ошибка при выполнении операции");
                setTimeout(() => setError(null), 3000);
            }
        };

        const handleBanUser = async (isBlocking) => {
            setError(null);
            setSuccessMessage(null);

            if (!patient?.phone) {
                setError("Номер телефона пользователя не указан");
                return;
            }

            try {
                await blockUser(patient.phone, isBlocking);
                await handleClickGetPatientInfo();
                setSuccessMessage(
                    `Пользователь успешно ${
                        isBlocking ? "заблокирован" : "разблокирован"
                    }`
                );
                setTimeout(() => setSuccessMessage(null), 3000);
            } catch (error) {
                setError(error.message || "Ошибка при выполнении операции");
                setTimeout(() => setError(null), 3000);
            }
        };

        const handleDeleteCardId = async () => {
            setError(null);
            setSuccessMessage(null);

            if (!patient?.phone) {
                setError("Номер телефона пользователя не указан");
                return;
            }

            try {
                await deleteCardId(patient.id);
                await handleClickGetPatientInfo();
                setSuccessMessage("Сопоставление с картой удалено");
                setTimeout(() => setSuccessMessage(null), 3000);
            } catch (error) {
                setError("Ошибка при удалении сопоставления");
                setTimeout(() => setError(null), 3000);
            }
        };

        const handleSaveChild = async (name, birthday) => {
            setError(null);
            setSuccessMessage(null);

            if (!name.trim()) {
                setError("Введите имя ребенка");
                return;
            }

            try {
                await saveOrUpdateChild(
                    patient.id,
                    name,
                    birthday,
                    editingChild?.id
                );
                await handleClickGetPatientInfo();
                setSuccessMessage(
                    editingChild ? "Ребенок обновлен" : "Ребенок добавлен"
                );
                setTimeout(() => setSuccessMessage(null), 3000);
                setEditingChild(null);
            } catch (error) {
                setError(error.message || "Ошибка при сохранении");
                setTimeout(() => setError(null), 3000);
            }
        };

        const handleDetachChild = async (childId) => {
            setError(null);
            setSuccessMessage(null);

            try {
                await detachChild(childId);
                await handleClickGetPatientInfo();
                setSuccessMessage("Ребенок отвязан от пациента");
                setTimeout(() => setSuccessMessage(null), 3000);
            } catch (error) {
                setError(error.message || "Ошибка при отвязке");
                setTimeout(() => setError(null), 3000);
            }
        };

        const handleChangeName = async (name) => {
            setError(null);
            setSuccessMessage(null);

            if (!name?.trim()) {
                setError("Введите имя");
                return;
            }

            try {
                const updatedUser = await change(patient.phone, name.trim(), patient?.name);
                if (updatedUser) {
                    user.setUser(updatedUser);
                }
                await handleClickGetPatientInfo();
                setSuccessMessage("Имя изменено");
                setTimeout(() => setSuccessMessage(null), 3000);
            } catch (error) {
                setError(error.message || "Ошибка при изменении имени");
                setTimeout(() => setError(null), 3000);
            }
        };

        const handleMenuOptionClick = (option) => {
            setError(null);
            setSuccessMessage(null);

            switch (option) {
                case "Начислить баллы":
                    setOperationType("addPoints");
                    setIsModalOpen(true);
                    break;
                case "Списать баллы":
                    setOperationType("reducePoints");
                    setIsModalOpen(true);
                    break;
                case "Заблокировать пользователя":
                    handleBanUser(true);
                    break;
                case "Разблокировать пользователя":
                    handleBanUser(false);
                    break;
                case "Удалить сопоставление с картой":
                    handleDeleteCardId();
                    break;
                case "Сопоставить с картой":
                    setIsMatchModalOpen(true);
                    break;
                case "Сопоставить по ID пациента":
                    setIsMatchByCardModalOpen(true);
                    break;
                case "Изменить имя":
                    setIsChangeNameModalOpen(true);
                    break;
                case "Выдать права администратора":
                    handleAddAdmin(true);
                    break;
                case "Убрать права администратора":
                    handleAddAdmin(false);
                    break;
                case "Добавить ребенка":
                    setEditingChild(null);
                    setIsChildModalOpen(true);
                    break;
                case "Отвязать ребенка от родителя":
                    handleDetachChild(patient.id);
                    break;
                default:
                    break;
            }
            setIsMenuOpen(false);
        };

        const getFilteredOptions = () => {
            return MENU_OPTIONS.filter((option) => {
                const hasCardId = !!patient?.cardId;

                if (option === "Начислить баллы" && !(user.access.isOwner || user.access.earnPoints)) return false;

                if (option === "Списать баллы" && !(user.access.isOwner || user.access.subtractPoints)) return false;

                if (option === "Сопоставить с картой" && hasCardId) return false;

                if (option === "Сопоставить по ID пациента" && hasCardId) return false;

                if (option === "Удалить сопоставление с картой" && !hasCardId) return false;

                if (option === "Разблокировать пользователя" && !patient?.isBlocked) return false;

                if (option === "Заблокировать пользователя" && patient?.isBlocked) return false;

                if (option === "Выдать права администратора" && patient?.isAdmin) return false;

                if (option === "Убрать права администратора" && !patient?.isAdmin) return false;

                if (option === "Добавить ребенка" && patient?.isChild) return false;

                if (option === "Отвязать ребенка от родителя" && !patient?.isChild) return false;

                return true;
            });
        };

        return (
            <Box display="flex" flexDirection="column" gap={1} zIndex={9999}>
                <Collapse in={!!error}>
                    <Alert
                        severity="error"
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => setError(null)}
                            >
                                <CloseIcon fontSize="inherit"/>
                            </IconButton>
                        }
                        sx={{mb: 1}}
                    >
                        {error}
                    </Alert>
                </Collapse>

                <Collapse in={!!successMessage}>
                    <Alert
                        severity="success"
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => setSuccessMessage(null)}
                            >
                                <CloseIcon fontSize="inherit"/>
                            </IconButton>
                        }
                        sx={{mb: 1}}
                    >
                        {successMessage}
                    </Alert>
                </Collapse>

                <Box display="flex" justifyContent="flex-end">
                    <Button
                        variant="contained"
                        ref={anchorRef}
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        endIcon={<ArrowDropDownIcon/>}
                    >
                        Действия
                    </Button>

                    <Popper
                        open={isMenuOpen}
                        anchorEl={anchorRef.current}
                        placement="top-end"
                        transition
                    >
                        {({TransitionProps}) => (
                            <Grow {...TransitionProps}>
                                <Paper elevation={3}>
                                    <ClickAwayListener onClickAway={() => setIsMenuOpen(false)}>
                                        <MenuList>
                                            {getFilteredOptions().map((option) => (
                                                <MenuItem
                                                    key={option}
                                                    onClick={() => handleMenuOptionClick(option)}
                                                >
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </MenuList>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>

                    <ModalMatchCard
                        id={patient.id}
                        open={isMatchModalOpen}
                        isBirthdayDisabled={clinic.disableUserBirthday}
                        handleClose={() => setIsMatchModalOpen(false)}
                        handleSubmit={handleMatchCardId}
                    />

                    <ModalMatchCardByPatientId
                        id={patient.id}
                        open={isMatchByCardModalOpen}
                        handleClose={() => setIsMatchByCardModalOpen(false)}
                        handleSubmit={handleMatchCardIdByPatientId}
                    />
                    <ModalPoints
                        type={operationType}
                        id={patient.id}
                        handleUpdatePoints={handleUpdatePoints}
                        openModal={isModalOpen}
                        setOpenModal={setIsModalOpen}
                    />

                    <ModalChildSettings
                        open={isChildModalOpen}
                        handleClose={() => {
                            setIsChildModalOpen(false);
                            setEditingChild(null);
                        }}
                        handleSubmit={handleSaveChild}
                        childData={editingChild}
                        isBirthdayDisabled={clinic.disableUserBirthday}
                    />

                    <ModalChangeName
                        open={isChangeNameModalOpen}
                        handleClose={() => setIsChangeNameModalOpen(false)}
                        handleSubmit={handleChangeName}
                        initialName={patient?.name || ""}
                    />
                </Box>
            </Box>
        );
    }
);

export default PatientSettings;
